import React, {useCallback, useEffect, useState} from 'react';
import {season} from "../../../models/season";
import {race} from "../../../models/race";
import {calendrierFiltersType} from "./CalendrierBox";
import DateService from "../../../service/DateService";
import {CalendrierDetailModal} from "./CalendrierDetailModal";
import {TicketIcon} from "../TicketIcon";
import {TableLineLoader} from "../loader/TableLineLoader";
import {ApiQueryParam, ApiService} from "../../../service/ApiService";
import {raceResultCollectionType, raceResultType} from "../../../types/Api/RaceResultType";
import {raceHydrator} from "../../../models/hydrator/raceHydrator";

export type CalendrierListeProps = {
  season: season;
  filters: calendrierFiltersType;
  setRace: (r: race) => void;
}

export const CalendrierListe = ({season, filters, setRace}: CalendrierListeProps) => {
  const [loading, setLoading] = useState(true as boolean);
  const [racesLoaded, setRacesLoaded] = useState([] as race[]);
  const [races, setRaces] = useState([] as race[]);
  const [raceDetail, setRaceDetail] = useState(undefined as undefined | race);

  const loadRaces = useCallback((): void => {
    setLoading(true);

    const apiFilters: ApiQueryParam = new ApiQueryParam();

    apiFilters.addFilter({
      op: 'eq',
      field: 'season_id',
      value: season.seasonId
    });

    apiFilters.setCount(0);

    const result: Promise<raceResultCollectionType> = ApiService.get('/race', apiFilters) as Promise<raceResultCollectionType>;

    result
      .then((raceResultCollection: raceResultCollectionType) => {
        setRacesLoaded(raceResultCollection._embedded.races.map((raceResult: raceResultType) => raceHydrator.hydrate(raceResult)));
      })
      .catch(() => {
        setRacesLoaded([]);
      });

    setLoading(false);
  }, [season.seasonId])

  useEffect(() => {
    const now = new Date();
    now.setHours(0, 0, 0);

    setRaces(racesLoaded.filter((r: race) => {
      const compare = DateService.compare(r.rideDate, now);

      return !(
        (compare === -1 && filters.past !== true) ||
        (filters.ranking && !r.ranking) ||
        (filters.ramble && r.ranking) ||
        (filters.paid && !r.paid) ||
        (filters.subsidized && !r.subsidized)
      );
    }));
  }, [filters.paid, filters.past, filters.ramble, filters.ranking, filters.subsidized, racesLoaded]);

  useEffect(() => {
    if (season) {
      loadRaces();
    }
  }, [season, loadRaces]);

  return (
    <>
      <table className="table table-responsive table-striped d-table d-md-none">
        <thead>
        <tr>
          <th className="w35px"/>
          <th/>
          <th className="w35px"/>
        </tr>
        </thead>
        <tbody>
        {loading && (
          <tr>
            <td colSpan={3} className="pt-4 pb-4 ps-5 pe-5">
              <TableLineLoader/>
            </td>
          </tr>
        )}
        {!loading && races.map((race: race, i: number) => (
          <tr key={i}>
            <td className="text-end align-middle">
              <i className="fa fa-ticket text-primary cursor-pointer" onClick={() => setRace(race)}/>
            </td>
            <td className="break-word">
              <span className="underlinee text-gray d-block mb-1">{DateService.toLocaleString(race.rideDate, {weekday: 'short', day: '2-digit', month: 'short'})}</span>
              {race.label}
            </td>
            <td className="align-middle">
              {race.description && (
                <i className="fa-solid fa-circle-info text-primary cursor-pointer" onClick={() => setRaceDetail(race)}/>
              )}
              {!race.description && race.link && (
                <a href={race.link} target="_blank" rel="noreferrer"><i className="fa-solid fa-link"/></a>
              )}
            </td>
          </tr>
        ))}
        {!loading && races.length === 0 && (
          <tr>
            <td colSpan={3} className="text-center italic">aucune sortie trouvée avec les filtres actuelles</td>
          </tr>
        )}
        </tbody>
      </table>

      <table className="table table-responsive table-striped d-none d-md-table">
        <thead>
        <tr>
          <th className="w35px"/>
          <th className="w110px"/>
          <th className="w35px"/>
          <th/>
          <th className="w170px text-center">Participation du club</th>
          <th className="w35px"/>
        </tr>
        </thead>
        <tbody>
        {loading && (
          <tr>
            <td colSpan={7} className="pt-4 pb-4 ps-5 pe-5">
              <TableLineLoader/>
            </td>
          </tr>
        )}

        {!loading && races.map((race: race, i: number) => (
          <tr key={i}>
            <td className="text-end">
              <TicketIcon onClick={() => setRace(race)} number={race.participants?.length}/>
            </td>
            <td className="text-center">{DateService.toLocaleString(race.rideDate, {weekday: 'short', day: '2-digit', month: 'short'})}</td>
            <td>
              {race.ranking ? (<i className="fa-solid fa-flag-checkered"/>) : (<i className="fa-solid fa-person-biking"/>)}
            </td>
            <td>{race.label}</td>
            <td className="text-center">
              {(race.paid || race.subsidized) && (
                <>
                  {race.paid && (
                    <><i className="fa-solid fa-money-bill-wave" title="Payée par le club"/> Payée</>
                  )}
                  {! race.paid && race.subsidized && (
                    <><i className="fa-solid fa-hand-holding-dollar" title="Subventionnée par le club"/> Subventionnée</>
                  )}
                </>
              )}
            </td>
            <td>
              {race.description && (
                <i className="fa-solid fa-circle-info text-primary cursor-pointer" onClick={() => setRaceDetail(race)}/>
              )}
              {!race.description && race.link && (
                <a href={race.link} target="_blank" rel="noreferrer"><i className="fa-solid fa-link"/></a>
              )}
            </td>
          </tr>
        ))}
        {!loading && races.length === 0 && (
          <tr>
            <td colSpan={7} className="text-center italic">aucune sortie trouvée avec les filtres actuelles</td>
          </tr>
        )}
        </tbody>
      </table>
      {raceDetail && (
        <CalendrierDetailModal race={raceDetail} close={() => setRaceDetail(undefined)}/>
      )}
    </>
  )
}
